<template>
  <div id="app" :style="appWrapperStyle">
    <router-view></router-view>
    <div class="dev-badge" v-if="devBadge">{{ devBadge }}</div>
  </div>
</template>
<script>
import { CommonLogger } from '../services/logger';
import { mapState } from 'vuex';
import { unleash } from '../services/featureFlags';
import utils from '../services/utils';
import eventDispatcher from '../events/dispatch';

export default {
  beforeMount() {
    if ((this.$route.meta && this.$route.meta.isPublic)) return;

    try {
      this.confirmValidAuth0UserLoggedIn();
      this.confirmRouteIsAuthorized();
      this.$store.commit('updateCurrentUser', this.currentUser);
    } catch (error) {
      CommonLogger.info(
        'Invalid User or Unauthorized Route in App.vue',
        {
          data: {
            jsonPayload: {
              error: (error && error.message),
              userEmail: (this.currentUser && this.currentUser.email),
            },
          },
        },
      );

      return this.$router.push({ name: 'login-redirect' });
    }
  },
  created() {
    window.addEventListener('resize', this.handleResize);
  },
  mounted() {
    eventDispatcher('pageView');
    this.unregisterServiceWorkers();
    this.initFeatureFlags();
    this.handleResize();
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    ...mapState({
      marginBehindNavBar: state => state.main.marginBehindNavBar,
    }),
    currentUserFromLocalStorage() {
      // If removed, new tabs/windows of Platform could open as unauthenticated and log a user out
      try {
        return JSON.parse(window.localStorage.getItem('mm-platform-user'));
      } catch (e) {
        return undefined;
      }
    },
    devBadge() {
      if (utils.isDev()) {
        return 'PLATFORM-DEV';
      } else if (utils.isPreviewChannel()) {
        return 'DEPLOY PREVIEW';
      }

      return '';
    },
    routeName() {
      return this.$route.name;
    },
    currentUser() {
      return this.$store.state.main.currentUser
        || this.currentUserFromLocalStorage
        || this.$auth.user;
    },
    defaultSelfServeView() {
      return this.$store.state.main.defaultSelfServeView;
    },
    appWrapperStyle() {
      return `margin-left: ${this.marginBehindNavBar}px`;
    },
  },
  methods: {
    confirmValidAuth0UserLoggedIn() {
      if (!this.$auth.isAuthenticated) {
        window.localStorage.setItem(
          'mm-platform-login-redirect-path',
          window.location.pathname,
        );

        throw new Error('User not authenticated');
      }
    },
    confirmRouteIsAuthorized() {
      const userData = this.currentUser;

      if (!userData || !userData.roles || !Object.keys(userData.roles).length) {
        return this.sendUnauthorizedUserToAccessDenied();
      }

      const routeMeta = this.$route.meta;

      if (routeMeta.selfServe || this.$route.name === 'callback') return;

      if (!userData.roles.COMMERCE_EDITOR && routeMeta.commerceEditorOnly)
        return this.sendUnauthorizedUserToDefault();
    },
    sendUnauthorizedUserToAccessDenied() {
      const { host, protocol } = window.location;
      const encodedURI = encodeURIComponent(this.$auth.user.email);

      this.$auth.logout({
        returnTo: `${protocol}//${host}/access-denied?email=${encodedURI}`,
      });
    },
    sendUnauthorizedUserToDefault() {
      return this.$router.replace({ path: this.defaultSelfServeView });
    },
    /**
     * We currently do not support PWA or service workers,
     * but we did have a no-op service worker running once.
     *
     * In order to prevent potential unwanted caching from users,
     * we unregister all service workers on app load.
     *
     * https://stackoverflow.com/a/33705250
     */
    async unregisterServiceWorkers() {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();

        for (const registration of registrations) {
          registration.unregister();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('cannot reach service worker(s)');
      }
    },
    initFeatureFlags() {
      unleash.setContextField('userId', this.currentUser.email);
      unleash.init();
    },
    handleResize() {
      const { innerWidth } = window;
      const isSmallScreen = innerWidth < 1000;

      if (isSmallScreen) {
        this.$store.commit('setMarginBehindNavBar', 80);
      } else {
        const navDrawer = document.querySelector('aside');

        if (navDrawer) {
          const { offsetWidth } = navDrawer;

          this.$store.commit('setMarginBehindNavBar', offsetWidth);
        }
      }
    },
  },
  watch: {
    routeName() {
      eventDispatcher('pageView');
    },
  },
};
</script>

<style lang="scss">
body {
  margin: 0;
  font-size: 2rem;
  font-family: Arial, "avenir next", avenir, helvetica, "helvetica neue", Ubuntu,
    "segoe ui", arial, sans-serif;
}
.page {
  text-align: center;
  code {
    background-color: #f0f0f0;
    padding: 3px 5px;
    border-radius: 2px;
  }
}
.mm-platform-sub-component {
  font-family: "Work Sans", sans-serif;
  position: absolute;
  left: 200px;
  right: 0;
  top: 60px;
  overflow: scroll;
  overflow-x: scroll;
  overflow-y: visible;
  padding-bottom: 100px;
}

.mm-platform-v2-sub-component {
  font-family: "Work Sans", sans-serif;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 80%;
}

.mm-platform-v2-inner-component {
  display: block;
}

@media only screen and (max-width: 770px) {
  .mm-platform-sub-component {
    display: block;
    left: 8px;
    right: 8px;
    top: 32px;
    -webkit-overflow-scrolling: touch;
    -webkit-transform: translateZ(0px);
    -webkit-transform: translate3d(0, 0, 0);
    -webkit-perspective: 1000;
  }
}

.mm-platform-inner-component {
  display: block;
  max-width: 770px;
  /* margin-left: 11%; */
}

.commerce-page-content {
  position: relative;
  min-height: 100vh;
  padding: 50px 64px;
  background: #F7FAFC;
}

@media only screen and (max-width: 770px) {
  .commerce-page-content {
    padding: 40px 10px;
  }
}

@media only screen and (max-width: 480px) {
  .commerce-page-content {
    padding: 20px 10px;
  }
}

.mobile-only {
  display: none !important;
}

@media only screen and (max-width: 770px) {
  .mobile-only {
    display: inline-block !important;
  }
}

.desktop-only {
  display: inline-block !important;
}

@media only screen and (max-width: 480px) {
  .desktop-only {
    display: none !important;
  }
}

@media only screen and (max-width: 1000px) {
  .mm-platform-inner-component {
    margin-left: 0px;
  }
}

@media only screen and (max-width: 770px) {
  .mm-platform-inner-component {
    margin-left: 0px;
  }
  .mm-platform-v2-sub-component,
  .mm-platform-v2-inner-component {
    max-width: 96%;
    margin: 0 auto;
  }
}

::-webkit-input-placeholder {
  /* Chrome/Opera/Safari */
  color: #dad8d8;
}
::-moz-placeholder {
  /* Firefox 19+ */
  color: #dad8d8;
}
:-ms-input-placeholder {
  /* IE 10+ */
  color: #dad8d8;
}
:-moz-placeholder {
  /* Firefox 18- */
  color: #dad8d8;
}

.dev-badge {
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 12px;
  background: #3d89ce;
  color: white;
  width: 116px;
  text-align: center;
  padding: 8px;
  margin: 8px;
  position: fixed;
  bottom: 0px;
  border-radius: 4px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.14);
}

.ui-state-highlight {
  background-color: lightgray;
  height: 120px;
  line-height: 1.2em;
  display: inline-block;
  width: 40%;
}

.view-slide-out .ui-state-highlight {
  height: 40px;
  width: 100%;
  margin: 0;
  background: transparent;
}

.sortable-variants .ui-state-highlight {
  width: 100%;
  background-color: #808080;
  border-radius: 8px;
  height: 5px;
}

.sortable-images .ui-state-highlight {
  width: 20%;
  height: 80px;
}

.player-assets .ui-state-highlight {
  width: 25%;
  height: 175px;
}
</style>
